package com.gloopgo.game.domain

import com.gloopgo.game.engine.FoodPellet
import com.gloopgo.game.engine.GameConstants
import com.gloopgo.game.engine.GloopPlayer
import com.gloopgo.game.engine.Vector2D
import kotlin.math.max

/**
 * Simple offline single-player bots: chase nearby pellets, flee larger blobs,
 * and opportunistically chase smaller edible opponents. Continuous steering
 * (not grid-locked) so it fits GloopGo's agar-style physics.
 */
object BlobAiController {
    /** Local engine ids for offline AI opponents, e.g. `ai_bot_1`. */
    const val AI_BOT_ID_PREFIX: String = "ai_bot_"
    const val BOT_COUNT: Int = 3
    const val RESPAWN_DELAY_MS: Long = 3000L
    const val SPAWN_MIN_SEPARATION: Float = 800f

    data class BotPersona(
        val name: String,
        val emoji: String,
        val color: Long,
    )

    val BOT_PERSONAS: List<BotPersona> = listOf(
        BotPersona(name = "BOT_GLOOP", emoji = "🫧", color = 0xFF00E5FFL),
        BotPersona(name = "BOT_BLOBBY", emoji = "🟣", color = 0xFFD500F9L),
        BotPersona(name = "BOT_OOZE", emoji = "🟢", color = 0xFF00E676L),
    )

    val BOT_NAMES: List<String> = BOT_PERSONAS.map { it.name }

    fun isAiPlayerId(id: String): Boolean = id.startsWith(AI_BOT_ID_PREFIX)

    /**
     * Returns a unit (or near-unit) steering vector for the bot this frame.
     */
    fun chooseDirection(
        self: GloopPlayer,
        pellets: Collection<FoodPellet>,
        otherPlayers: Collection<GloopPlayer>,
        worldWidth: Float = GameConstants.WORLD_WIDTH,
        worldHeight: Float = GameConstants.WORLD_HEIGHT,
    ): Vector2D {
        val cog = self.centerOfGravity
        val myMass = max(1f, self.totalMass)
        var steer = Vector2D.ZERO

        // Soft wall avoidance — push inward near borders
        val margin = max(120f, self.boundingRadius * 3f)
        if (cog.x < margin) steer += Vector2D(1f, 0f) * ((margin - cog.x) / margin) * 2.5f
        if (cog.x > worldWidth - margin) {
            steer += Vector2D(-1f, 0f) * ((cog.x - (worldWidth - margin)) / margin) * 2.5f
        }
        if (cog.y < margin) steer += Vector2D(0f, 1f) * ((margin - cog.y) / margin) * 2.5f
        if (cog.y > worldHeight - margin) {
            steer += Vector2D(0f, -1f) * ((cog.y - (worldHeight - margin)) / margin) * 2.5f
        }

        // Threat / prey response
        for (other in otherPlayers) {
            if (!other.isAlive || other.cells.isEmpty() || other.id == self.id) continue
            val otherCog = other.centerOfGravity
            val dist = cog.distanceTo(otherCog).coerceAtLeast(1f)
            val otherMass = other.totalMass
            when {
                otherMass >= myMass * GameConstants.EAT_MASS_ADVANTAGE -> {
                    // Flee larger blobs — stronger when close
                    val fleeWeight = (1800f / dist).coerceIn(0.4f, 6f)
                    steer += (cog - otherCog).normalized() * fleeWeight
                }
                myMass >= otherMass * GameConstants.EAT_MASS_ADVANTAGE -> {
                    // Chase edible smaller blobs
                    val chaseWeight = (900f / dist).coerceIn(0.2f, 3.5f)
                    steer += (otherCog - cog).normalized() * chaseWeight
                }
                else -> {
                    // Soft separation from similar-sized blobs
                    if (dist < 400f) {
                        steer += (cog - otherCog).normalized() * 0.35f
                    }
                }
            }
        }

        // Chase nearest non-hazard pellet (weaker than combat steering)
        val nearestPellet = pellets
            .asSequence()
            .filter { !it.isHazard }
            .minByOrNull { cog.distanceSquaredTo(it.position) }
        if (nearestPellet != null) {
            val dist = cog.distanceTo(nearestPellet.position).coerceAtLeast(1f)
            val pelletWeight = (600f / dist).coerceIn(0.15f, 1.8f)
            steer += (nearestPellet.position - cog).normalized() * pelletWeight
        }

        val len = steer.length()
        return if (len > 0.001f) {
            // Cap intensity so bots don't always run full speed
            val capped = (len / 2.2f).coerceIn(0.35f, 1f)
            steer.normalized() * capped
        } else {
            // Idle drift so they don't freeze if alone with no pellets
            Vector2D(0.25f, 0f)
        }
    }
}
